'use client';

import React, { useState, useEffect, useTransition } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter, usePathname } from '@/i18n/navigation';
import { SearchIcon, GroupIcon, LocateIcon, FilterXIcon } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Combobox } from '@/components/ui/combobox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { CategoryType } from '@/types/category';
import { VehicleGroupType } from '@/types/vehicle-group';
import { useAppLocale, useBrands, useModels } from '@/hooks';
import { useProductsLoadingStore } from '@/store';
import { getLocalizedValue, getResizedPictureUrl } from '@/lib/utils';
import { BrandType } from '@/types/brand';
import { ModelType } from '@/types/model';
type PropsType = {
  activeCategory?: CategoryType | undefined;
  vehicleGroups?: VehicleGroupType[];
  selectedVehicleGroups?: VehicleGroupType[];
  brands?: BrandType[];
  selectedBrands?: BrandType[];
  models?: ModelType[];
  selectedModels?: ModelType[];
  q?: string;
};
const ProductsFilter = ({
  activeCategory,
  vehicleGroups,
  selectedVehicleGroups,
  brands,
  selectedBrands,
  models,
  selectedModels,
  q
}: PropsType) => {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const {
    backendLocale
  } = useAppLocale();

  // Filter/search navigations change only search params, which don't trigger the route-level
  // loading.tsx. Run them in a transition and mirror the pending flag into a store so the products
  // list can show its skeleton while the new results load.
  const [isNavigating, startNavigation] = useTransition();
  const setProductsLoading = useProductsLoadingStore(state => state.setPending);
  useEffect(() => {
    setProductsLoading(isNavigating);
  }, [isNavigating, setProductsLoading]);

  // Combobox states - initialize from selected props if available
  const [vehicleGroup, setVehicleGroup] = useState<string[]>(selectedVehicleGroups?.map(vg => vg.id) || []);
  const [brand, setBrand] = useState<string[]>(selectedBrands?.map(b => b.id) || []);
  const [model, setModel] = useState<string[]>(selectedModels?.map(m => m.id) || []);
  const [keyword, setKeyword] = useState(q || '');

  // Track if a user has interacted with vehicle groups or brands (to determine if we should fetch)
  const [hasVehicleGroupChanged, setHasVehicleGroupChanged] = useState(false);
  const [hasBrandChanged, setHasBrandChanged] = useState(false);

  // Fetch brands when vehicle groups change using a custom hook
  // Only enabled when the user has changed vehicle groups
  const {
    data: dynamicBrandsData,
    isLoading: isBrandsLoading
  } = useBrands({
    vehicleGroupIds: vehicleGroup,
    categoryIds: activeCategory?.id ? [activeCategory.id] : [],
    queryEnabled: hasVehicleGroupChanged && vehicleGroup.length > 0
  });
  const dynamicBrands: BrandType[] = dynamicBrandsData || brands || [];

  // Fetch models when brands change using a custom hook
  // Only enabled when the user has changed brands
  const {
    data: dynamicModelsData,
    isLoading: isModelsLoading
  } = useModels({
    vehicleGroupIds: vehicleGroup,
    categoryIds: activeCategory?.id ? [activeCategory.id] : [],
    brandIds: brand,
    queryEnabled: hasBrandChanged && brand.length > 0
  });
  const dynamicModels: ModelType[] = dynamicModelsData || models || [];

  // Update states when selected props change (from URL parameters)
  useEffect(() => {
    if (selectedVehicleGroups) {
      setVehicleGroup(selectedVehicleGroups.map(vg => vg.id));
    }
  }, [selectedVehicleGroups]);
  useEffect(() => {
    if (selectedBrands) {
      setBrand(selectedBrands.map(b => b.id));
    }
  }, [selectedBrands]);
  useEffect(() => {
    if (selectedModels) {
      setModel(selectedModels.map(m => m.id));
    }
  }, [selectedModels]);
  useEffect(() => {
    setKeyword(q || '');
  }, [q]);

  // When vehicle groups change, remove selected brands that are not in the new brands list
  useEffect(() => {
    if (brand.length > 0 && dynamicBrands.length > 0) {
      const availableBrandIds = dynamicBrands.map(b => b.id);
      const validBrands = brand.filter(brandId => availableBrandIds.includes(brandId));
      if (validBrands.length !== brand.length) {
        setBrand(validBrands);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicBrands]);

  // When brands change, remove selected models that are not in the new models list
  useEffect(() => {
    if (model.length > 0 && dynamicModels.length > 0) {
      const availableModelIds = dynamicModels.map(m => m.id);
      const validModels = model.filter(modelId => availableModelIds.includes(modelId));
      if (validModels.length !== model.length) {
        setModel(validModels);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicModels]);

  // When vehicle groups are cleared, reset brands and models
  useEffect(() => {
    if (vehicleGroup.length === 0 && brand.length > 0) {
      setBrand([]);
      setModel([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleGroup.length]);

  // When brands are cleared, reset models
  useEffect(() => {
    if (brand.length === 0 && model.length > 0) {
      setModel([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand.length]);

  // Prepare vehicle groups options for combobox
  const vehicleGroupOptions = (vehicleGroups || []).map(group => ({
    label: getLocalizedValue(group, 'title', backendLocale) || group.id,
    value: group.id,
    image: group.picture ? getResizedPictureUrl(group.picture.path, 50, 50) : undefined,
    icon: !group.picture ? <GroupIcon className="h-5 w-5" /> : undefined
  }));

  // Prepare brands options for combobox - recalculate when dynamicBrands changes
  const brandOptions = (dynamicBrands || []).map(brand => ({
    label: brand.name,
    value: brand.id,
    image: brand.picture ? getResizedPictureUrl(brand.picture.path, 50, 50) : undefined,
    icon: !brand.picture ? <LocateIcon className="h-5 w-5" /> : undefined
  }));

  // Prepare models options for combobox
  const modelOptions = (dynamicModels || []).map(model => ({
    label: model.name,
    value: model.id
  }));
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    // Build query parameters from selected filters
    const params = new URLSearchParams();

    // Get slugs for selected vehicle groups (applications)
    if (vehicleGroup.length > 0) {
      const selectedVehicleGroupSlugs = (vehicleGroups || []).filter(vg => vehicleGroup.includes(vg.id)).map(vg => getLocalizedValue(vg, 'slug', backendLocale)).filter(slug => slug); // Remove empty slugs

      if (selectedVehicleGroupSlugs.length > 0) {
        params.set('applications', selectedVehicleGroupSlugs.join(','));
      }
    }

    // Get slugs for selected brands
    if (brand.length > 0) {
      const selectedBrandSlugs = dynamicBrands.filter(b => brand.includes(b.id)).map(b => b.slug).filter(slug => slug); // Remove empty slugs

      if (selectedBrandSlugs.length > 0) {
        params.set('brands', selectedBrandSlugs.join(','));
      }
    }

    // Get slugs for selected models
    if (model.length > 0) {
      const selectedModelSlugs = dynamicModels.filter(m => model.includes(m.id)).map(m => m.slug).filter(slug => slug); // Remove empty slugs

      if (selectedModelSlugs.length > 0) {
        params.set('models', selectedModelSlugs.join(','));
      }
    }

    // Add keyword if provided
    if (keyword.trim()) {
      params.set('q', keyword.trim());
    }

    // Navigate to the same pathname with new query parameters
    const queryString = params.toString();
    startNavigation(() => router.push(`${pathname}${queryString ? `?${queryString}` : ''}`));
  };

  // Whether any filter is currently applied — controls the "clear filters" button visibility.
  const hasActiveFilters = vehicleGroup.length > 0 || brand.length > 0 || model.length > 0 || keyword.trim().length > 0;

  // Whether the form differs from the already-applied (URL) filters. When it doesn't, searching would
  // be a no-op, so the search button is disabled.
  const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every(value => b.includes(value));
  const hasFilterChanged = !sameSet(vehicleGroup, selectedVehicleGroups?.map(vg => vg.id) ?? []) || !sameSet(brand, selectedBrands?.map(b => b.id) ?? []) || !sameSet(model, selectedModels?.map(m => m.id) ?? []) || keyword.trim() !== (q ?? '').trim();
  const handleClearFilters = () => {
    setVehicleGroup([]);
    setBrand([]);
    setModel([]);
    setKeyword('');
    setHasVehicleGroupChanged(false);
    setHasBrandChanged(false);
    startNavigation(() => router.push(pathname));
  };
  return <div className="space-y-5">
      <div className="flex flex-col space-y-3">
        {/* Search Form */}
        <form onSubmit={handleSearch}>
          <div className="m-auto grid grid-cols-1 gap-4 sm:grid-cols-1 md:grid-cols-6 xl:grid-cols-6">
            {/* Vehicle Group Combobox */}
            <div className="col-span-1 md:col-span-2 xl:col-span-1">
              <label htmlFor="products-filter-vehicle-group" className="mb-1.5 block text-sm font-medium">
                {t('application')}
              </label>
              <Combobox id="products-filter-vehicle-group" options={vehicleGroupOptions} value={vehicleGroup} onValueChange={val => {
              setVehicleGroup(val as string[]);
              setHasVehicleGroupChanged(true);
            }} placeholder={t('select')} searchPlaceholder={t('search')} emptyText={t('no_results_found')} multiple clearable />
            </div>

            {/* Brand Combobox */}
            <div className="col-span-1 md:col-span-2 xl:col-span-1">
              <label htmlFor="products-filter-brand" className="mb-1.5 block text-sm font-medium">
                {t('brand')}
              </label>
              <Combobox id="products-filter-brand" options={brandOptions} value={brand} onValueChange={val => {
              setBrand(val as string[]);
              setHasBrandChanged(true);
            }} placeholder={t('select_brand')} searchPlaceholder={t('search')} emptyText={t('no_results_found')} disabled={isBrandsLoading} multiple clearable />
            </div>

            {/* Model Combobox */}
            <div className="col-span-1 md:col-span-2 xl:col-span-1">
              <label htmlFor="products-filter-model" className="mb-1.5 block text-sm font-medium">
                {t('model')}
              </label>
              <Combobox id="products-filter-model" options={modelOptions} value={model} onValueChange={val => setModel(val as string[])} placeholder={t('select_model')} searchPlaceholder={t('search')} emptyText={t('no_results_found')} disabled={brand.length === 0 || isModelsLoading} multiple clearable />
            </div>

            {/* Search Keyword Input */}
            <div className="colspan-4 md:col-span-4 xl:col-span-2">
              <label htmlFor="products-filter-keyword" className="mb-1.5 block text-sm font-medium">
                {t('keyword')}
              </label>
              <Input id="products-filter-keyword" type="search" value={keyword} onChange={e => setKeyword(e.target.value)} placeholder={t('search_keyword')} />
            </div>

            {/* Search Button (+ clear filters when a filter is active) */}
            <div className="col-span-1 flex items-end gap-2 md:col-span-2 xl:col-span-1">
              <Button variant="brand" type="submit" disabled={!hasFilterChanged} className="flex-1 uppercase">
                {t('search')}
                <SearchIcon />
              </Button>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button type="button" variant="outline" size="icon" onClick={handleClearFilters} disabled={!hasActiveFilters} aria-label={t('clear_filters')} className="shrink-0 border-red-300 text-red-600 hover:border-red-400 hover:bg-red-50 hover:text-red-700">
                    <FilterXIcon />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('clear_filters')}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </form>
      </div>

      <Separator />
    </div>;
};
export default ProductsFilter;